import { useState, useRef, useEffect } from 'react';
import BobineLogo from './BobineLogo';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSelector from './LanguageSelector';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const VerifyEmail = ({ userEmail, token, onVerified }) => {
  const { t } = useLanguage();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  const inputRefs = useRef([]);

  const handleLogout = () => {
    localStorage.removeItem('bobine_token');
    localStorage.removeItem('bobine_user');
    window.location.href = '/compte';
  };

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    const v = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!v && value) return;
    const next = [...digits];
    next[index] = v.slice(-1);
    setDigits(next);
    setError(null);
    if (v && index < 5) inputRefs.current[index + 1]?.focus();
    // Auto-submit when all 6 filled
    if (v && index === 5 && next.every(d => d)) {
      submitCode(next.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
      submitCode(pasted);
    }
  };

  const submitCode = async (code) => {
    setVerifying(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Code de vérification incorrect');
      }
      onVerified();
    } catch (e) {
      setError(e.message);
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg(null);
    try {
      const res = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur');
      setResendMsg(t('verify_resent'));
    } catch {
      setResendMsg(t('error'));
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 6) { setError('Veuillez entrer les 6 caractères'); return; }
    submitCode(code);
  };

  // Mask email: s***e@gmail.com
  const maskedEmail = userEmail
    ? userEmail.replace(/^(.)(.*)(@.*)$/, (_, f, m, d) => f + '*'.repeat(Math.min(m.length, 4)) + d)
    : '';

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#000' }}>
      <div style={{
        maxWidth: 440, width: '92%', padding: '48px 36px', textAlign: 'center',
        backgroundColor: '#0a0a0a', borderRadius: 20,
        border: '1px solid rgba(212,175,55,0.15)',
        position: 'relative',
      }}>
        <BobineLogo size={22} />
        <div style={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSelector />
        </div>

        {/* Mail icon */}
        <div style={{ margin: '28px 0 16px' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto' }}>
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="M22 4L12 13 2 4" />
          </svg>
        </div>

        <h2 data-testid="verify-title" style={{
          fontFamily: "'Georgia', serif", fontSize: 22, color: '#D4AF37',
          marginBottom: 10,
        }}>
          {t('verify_title')}
        </h2>
        <p style={{
          fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0',
          lineHeight: 1.6, marginBottom: 28,
        }}>
          {t('verify_desc')}<br />
          <span style={{ color: '#D4AF37' }}>{maskedEmail}</span>
        </p>

        <form onSubmit={handleSubmit}>
          <div data-testid="verify-code-inputs" style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                data-testid={`verify-digit-${i}`}
                type="text"
                maxLength={1}
                value={d}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                onPaste={i === 0 ? handlePaste : undefined}
                style={{
                  width: 48, height: 56, textAlign: 'center',
                  fontFamily: "'Georgia', serif", fontSize: 22, fontWeight: 600,
                  color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.04)',
                  border: d ? '2px solid rgba(212,175,55,0.5)' : '1px solid rgba(212,175,55,0.15)',
                  borderRadius: 10, outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#D4AF37'}
                onBlur={e => e.target.style.borderColor = d ? '#D4AF37' : '#D4AF37'}
              />
            ))}
          </div>

          {error && (
            <p data-testid="verify-error" style={{
              fontFamily: "'Georgia', serif", fontSize: 13, color: '#c0392b',
              marginBottom: 14,
            }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={verifying || digits.some(d => !d)}
            data-testid="verify-submit-button"
            style={{
              width: '100%', padding: '14px', borderRadius: 10, cursor: 'pointer',
              fontFamily: "'Georgia', serif", fontSize: 15, fontWeight: 600,
              background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none',
              opacity: verifying || digits.some(d => !d) ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {verifying ? t('verify_confirming') : t('verify_confirm')}
          </button>
        </form>

        <div style={{ marginTop: 24, borderTop: '1px solid rgba(212,175,55,0.08)', paddingTop: 20 }}>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#FAF3E0', marginBottom: 8 }}>
            {t('verify_not_received')}
          </p>
          <button
            onClick={handleResend}
            disabled={resending}
            data-testid="verify-resend-button"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37',
              textDecoration: 'underline', textUnderlineOffset: 3,
              opacity: resending ? 0.5 : 1,
            }}
          >
            {resending ? t('verify_resending') : t('verify_resend')}
          </button>
          {resendMsg && (
            <p data-testid="verify-resend-msg" style={{
              fontFamily: "'Georgia', serif", fontSize: 12, marginTop: 8,
              color: resendMsg === t('verify_resent') ? '#D4AF37' : '#c0392b',
            }}>
              {resendMsg}
            </p>
          )}
        </div>

        {/* Bouton de déconnexion */}
        <button
          onClick={handleLogout}
          data-testid="verify-logout-button"
          style={{
            marginTop: 20, background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: "'Georgia', serif", fontSize: 12,
            color: '#FAF3E0', transition: 'color 0.2s',
          }}
          onMouseEnter={e => e.target.style.color = '#FAF3E0'}
          onMouseLeave={e => e.target.style.color = '#FAF3E0'}
        >
          {t('verify_logout')}
        </button>
      </div>
    </div>
  );
};

export default VerifyEmail;
